import { AsyncPipe, DatePipe } from '@angular/common';
import { Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialog as MatDialog,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { MatLegacyFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyInputModule } from '@angular/material/legacy-input';
import { MatLegacyMenuModule } from '@angular/material/legacy-menu';
import { MatLegacyRadioModule } from '@angular/material/legacy-radio';
import { MatLegacyTooltipModule } from '@angular/material/legacy-tooltip';
import { DialogDeleteGenericComponent } from '@app/shared/components/dialog-delete-generic/dialog-delete-generic.component';
import { TaskInstanceExtended } from '@app/shared/interfaces';
import { DeviceDetectionService } from '@app/shared/services/device-detection/device-detection.service';
import { PropertiesService, PropertyIncludes } from '@app/shared/services/properties/properties.service';
import { PusherService } from '@app/shared/services/pusher/pusher.service';
import { TasksService } from '@app/shared/services/tasks/tasks.service';
import { ToastNotificationsService } from '@app/shared/services/toast-notifications/toast-notifications.service';
import { UiModule } from '@app/ui/ui.module';
import {
  AlertComponent,
  BadgeComponent,
  ButtonComponent,
  CardComponent,
  ChecklistBuilderComponent,
  IconComponent,
  LoadingSpinnerComponent,
  ModalFullComponent,
} from '@ui';
import { Logger } from '@utils';
import { format, setHours, setMinutes } from 'date-fns';
import getHours from 'date-fns/get_hours';
import getMinutes from 'date-fns/get_minutes';
import { fadeInUpAnimation } from 'src/animations';
import { CleanerCardComponent } from '../../task-rules/cleaner-card/cleaner-card.component';
import { TaskChecklistViewerComponent } from '../task-checklist-viewer/task-checklist-viewer.component';
import { TaskLocationMapComponent } from '../task-location-map/task-location-map.component';
import { TaskProblemsViewerComponent } from '../task-problems-viewer/task-problems-viewer.component';
import { TaskProgressTrackerComponent } from '../task-progress-tracker/task-progress-tracker.component';
import { TaskProgressService } from '../task-progress.service';
import { TaskEditModalData, TaskEditModalResult } from './task-edit-modal.service';

type Mode = 'overview' | 'access' | 'checklist-editor' | 'checklist-viewer' | 'problems';

@Component({
  selector: 'sbnb-task-edit-modal',
  templateUrl: './task-edit-modal.component.html',
  standalone: true,
  imports: [
    AsyncPipe,
    ModalFullComponent,
    BadgeComponent,
    CardComponent,
    ButtonComponent,
    LoadingSpinnerComponent,
    MatLegacyFormFieldModule,
    MatLegacyInputModule,
    MatLegacyMenuModule,
    MatLegacyRadioModule,
    MatLegacyTooltipModule,
    DatePipe,
    AlertComponent,
    TaskProgressTrackerComponent,
    TaskLocationMapComponent,
    ChecklistBuilderComponent,
    TaskChecklistViewerComponent,
    TaskProblemsViewerComponent,
    CleanerCardComponent,
    UiModule,
    IconComponent,
  ],
  animations: [fadeInUpAnimation],
})
export class TaskEditModalComponent {
  readonly #dialogRef = inject(MatDialogRef<TaskEditModalComponent>);
  readonly #taskService = inject(TasksService);
  readonly #propertiesService = inject(PropertiesService);
  readonly #deviceDetectionService = inject(DeviceDetectionService);
  readonly #dialog = inject(MatDialog);
  readonly #pusherService = inject(PusherService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #toastService = inject(ToastNotificationsService);
  readonly #taskProgressService = inject(TaskProgressService);

  readonly isMobile$ = this.#deviceDetectionService.$isMobile;

  readonly mode = signal<Mode>('overview');

  readonly data = inject<TaskEditModalData>(MAT_DIALOG_DATA);
  readonly task = signal<TaskInstanceExtended | null>(null);
  readonly propertyWithSmartlocks = signal<{ has_smartlocks: boolean } | null>(null);
  readonly loadingProperty = signal<boolean>(false);

  readonly startHour = signal<number>(0);
  readonly startMin = signal<number>(0);
  readonly endHour = signal<number>(0);
  readonly endMin = signal<number>(0);

  // @TODO: Alert to top up balance is currently hardcoded
  readonly showTopUpBalanceAlert = computed(() => {
    return false;
  });

  readonly summaryText = computed(() => {
    const task = this.task();
    if (!task) return '';

    // Get property address
    const propertyName = task.property?.name || 'the property';

    // Check task progress status
    if (task.progress_status === 'on_the_way') {
      return `Cleaner is on the way to ${propertyName}`;
    }

    if (task.progress_status === 'in_progress') {
      return `Cleaning is in progress at ${propertyName}`;
    }

    if (task.progress_status === 'completed') {
      return `Cleaning complete at ${propertyName}`;
    }

    // For 'arrived' status or no status, show the starting time
    // Get time until start
    const timeUntil = task.time_until || 'soon';

    // Format start time (extract time from start_date)
    const startDate = new Date(task.start_date);
    const hours = startDate.getHours().toString().padStart(2, '0');
    const minutes = startDate.getMinutes().toString().padStart(2, '0');
    const formattedTime = `${hours}:${minutes}`;

    return `Starting ${timeUntil} (${formattedTime}) at ${propertyName}`;
  });

  readonly checklistSummary = computed(() => {
    const checklist = this.task()?.checklist;
    if (!checklist?.groups?.length) return '';
    return checklist.groups.map((group) => group.title).join(', ');
  });

  readonly checklistNumberOfItems = computed(() => {
    const checklist = this.task()?.checklist;
    if (!checklist?.groups?.length) return 0;
    return checklist.groups.reduce((total, group) => total + (group.items?.length || 0), 0);
  });

  readonly checklistNumberOfCompletedItems = computed(() => {
    const checklist = this.task()?.checklist;
    if (!checklist?.groups?.length) return 0;
    return checklist.groups.reduce((total, group) => {
      const completedInGroup = group.items?.filter((item) => item.status === 'done').length || 0;
      return total + completedInGroup;
    }, 0);
  });

  readonly propertyAccessSummary = computed(() => {
    const task = this.task();
    if (!task) return '';

    const accessType = task.create_operation_codes ? 'Smart lock code' : 'Other access';
    const instructions = task.note ? `, Instructions: "${task.note}"` : '';

    return `${accessType}${instructions}`;
  });

  readonly problemSummary = computed(() => {
    const task = this.task();
    if (!task?.problems) return '';

    return task.problems.map((problem) => problem.title).join(', ');
  });

  readonly problemNumberOfItems = computed(() => {
    const problems = this.task()?.problems;
    if (!problems?.length) return 0;
    return problems.length;
  });

  readonly progressStatusLabel = computed(() => {
    const task = this.task();
    if (!task || !task.progress_status) return '';
    return this.#taskProgressService.getStatusLabel(task.progress_status);
  });

  readonly loadingTask = signal<boolean>(true);
  readonly saving = signal<boolean>(false);

  // Can the checklist still be edited by the host, or are we now in readonly viewer mode?
  // @TODO: This should be a proper logic, not hardcoded
  readonly isChecklistEditable = computed(() => {
    return true;
  });

  // Smart lock functionality - determines if smartlock can be used
  readonly smartLockState = computed(() => {
    const property = this.propertyWithSmartlocks();
    const loadingProperty = this.loadingProperty();

    if (loadingProperty) {
      return { canUseSmartLock: false, isLoading: true };
    }

    if (!property) {
      return { canUseSmartLock: false, isLoading: false };
    }

    return {
      canUseSmartLock: property.has_smartlocks,
      isLoading: false,
    };
  });

  constructor() {
    this.#fetchTaskDetails(this.data.taskUuid);
    this.#registerForPusherTaskUpdates();

    // Set initial mode if provided
    if (this.data.initialMode) {
      this.mode.set(this.data.initialMode);
    }
  }

  back(): void {
    if (this.mode() !== 'overview') {
      this.mode.set('overview');
      this.#scrollToTop();
    } else {
      this.close();
    }
  }

  close(): void {
    this.#dialogRef.close();
  }

  save(): void {
    this.saving.set(true);

    const task = this.task();
    if (!task) return;

    let start = new Date(this.task().start_date);
    let end = new Date(this.task().end_date);

    start = setHours(start, this.startHour());
    start = setMinutes(start, this.startMin());
    end = setHours(end, this.endHour());
    end = setMinutes(end, this.endMin());

    const payload = {
      // Access
      create_operation_codes: task.create_operation_codes,
      note: task.note,

      // Checklist
      checklist: task.checklist,

      // Time
      start_date: format(start, 'YYYY-MM-DD HH:mm:ss'),
      end_date: format(end, 'YYYY-MM-DD HH:mm:ss'),
    };

    this.#taskService.updateTask(task.uuid, payload).subscribe({
      next: (res) => {
        this.saving.set(false);

        if (!res.error && res.data) {
          const result: TaskEditModalResult = {
            action: 'updated',
            task: res.data,
          };
          this.#dialogRef.close(result);
          this.#toastService.success('Task updated');
        }
      },
      error: () => {
        this.saving.set(false);
      },
    });
  }

  updateTaskStartDate(date: string | string[]): void {
    this.task.set({
      ...this.task(),
      start_date: date as string,
    });
  }

  updateTaskEndDate(date: string | string[]): void {
    this.task.set({
      ...this.task(),
      end_date: date as string,
    });
  }

  startUpdated(newTime: { hours: number; minutes: number }) {
    this.startHour.set(newTime.hours);
    this.startMin.set(newTime.minutes);
  }

  endUpdated(newTime: { hours: number; minutes: number }) {
    this.endHour.set(newTime.hours);
    this.endMin.set(newTime.minutes);
  }

  updateTaskNote(note: string) {
    this.task.set({
      ...this.task(),
      note,
    });
  }

  openChecklist() {
    this.setMode(this.isChecklistEditable() ? 'checklist-editor' : 'checklist-viewer');
  }

  openProblems() {
    this.setMode('problems');
  }

  openMessages() {
    const task = this.task();

    if (!task?.uuid) {
      Logger.error('Cannot open messages - no task UUID available');
      return;
    }

    // Clear the unread message count in local state when we open the conversation
    this.task.set({
      ...this.task(),
      operations_chat: {
        ...this.task()?.operations_chat,
        operations_thread_new_messages_count: 0,
      },
    });

    import('../task-conversation/task-conversation.component').then(({ TaskConversationComponent }) => {
      const isMobile = window.innerWidth < 768; // md breakpoint

      this.#dialog.open(TaskConversationComponent, {
        width: isMobile ? '100vw' : '600px',
        height: isMobile ? '100vh' : '80vh',
        maxWidth: isMobile ? '100vw' : '600px',
        position: isMobile ? { top: '0', left: '0' } : undefined,
        panelClass: isMobile ? 'mobile-fullscreen-dialog' : '',
        data: {
          taskUuid: task.uuid,
          cleanerName: task.marketplace_teammate?.name || 'Cleaner',
          taskType: task.task_type_string,
        },
      });
    });
  }

  setMode(mode: Mode) {
    this.mode.set(mode);
    this.#scrollToTop();
  }

  getCleaningSuggestionMessage(): string {
    return this.#taskService.formatAISuggestionsMessage(this.task()?.ai_suggestions?.todos);
  }

  addSuggestedTasksToChecklist = () => {
    const todos = this.task()?.ai_suggestions?.todos;
    if (!todos?.length) {
      return;
    }

    // Create new checklist items from the todos with todo_uuid attribute
    const newItems = todos.map((todo) => ({
      content: `➡️ ${todo.content}`,
      status: null,
      todo_uuid: todo.uuid,
    }));

    // Create the "Requires Attention" group with the new items
    const requiresAttentionGroup = {
      title: `‼️ Requires Attention`,
      items: newItems,
      ai_suggested_group: true,
    };

    // Update the task's checklist by adding the new group at the beginning
    if (this.task()) {
      this.task.update((task) => {
        if (!task) return task;

        const currentChecklist = task.checklist || {
          id: `checklist-${task.uuid}`,
          title: 'Cleaning Checklist',
          groups: [],
        };

        const updatedChecklist = {
          ...currentChecklist,
          groups: [requiresAttentionGroup, ...currentChecklist.groups],
        };

        return {
          ...task,
          checklist: updatedChecklist,
        };
      });
    }

    // Mark the todos as added to checklist in the API
    const updates = todos.map((todo) => ({
      uuid: todo.uuid,
      action: 'task_checklist' as const,
      task_uuid: this.task()?.uuid,
    }));

    this.#taskService.updateTodos(updates).subscribe({
      next: () => {
        Logger.log('Successfully marked todos for addition to checklist');
        // Clear the AI suggestions after adding them
        if (this.task()) {
          this.task.update((task) => ({
            ...task!,
            ai_suggestions: null,
          }));
        }
      },
      error: (error) => {
        Logger.error('Failed to mark todos for checklist', error);
        this.#toastService.error('Failed to add suggested tasks');
      },
    });
  };

  dismissCleaningSuggestions() {
    const todos = this.task()?.ai_suggestions?.todos;
    if (!todos?.length) {
      return;
    }

    const updates = todos.map((todo) => ({
      uuid: todo.uuid,
      action: 'dismissed' as const,
    }));

    this.#taskService.updateTodos(updates).subscribe({
      next: () => {
        // Clear the AI suggestions after dismissing them
        if (this.task()) {
          this.task.update((task) => ({
            ...task!,
            ai_suggestions: null,
          }));
        }
      },
      error: (error) => {
        Logger.error('Failed to dismiss cleaning suggestions', error);
      },
    });
  }

  removeTaskPrompt() {
    const dialogRef = this.#dialog.open(DialogDeleteGenericComponent, {
      width: '720px',
      data: {
        name: 'this cleaning task',
        action: 'Remove',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.#removeTask();
      }
    });
  }

  #removeTask() {
    const taskUuid = this.task()?.uuid;
    if (!taskUuid) {
      Logger.error('Cannot delete task - no UUID available');
      return;
    }

    this.#taskService.deleteTask(taskUuid).subscribe(() => {
      const result: TaskEditModalResult = {
        action: 'deleted',
        taskUuid: taskUuid,
      };
      this.#dialogRef.close(result);
      this.#toastService.success('Task removed');
    });
  }

  #scrollToTop() {
    setTimeout(() => {
      const dialog = document.querySelector('[role="dialog"]');

      if (dialog) {
        dialog.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }
    }, 100);
  }

  #registerForPusherTaskUpdates(): void {
    this.#pusherService
      .getTaskUpdatesForUuid(this.data.taskUuid)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((updatedTask) => {
        // Sanity check: ensure we're updating the correct task
        if (updatedTask.uuid !== this.data.taskUuid) {
          Logger.error(
            `Task UUID mismatch in Pusher update. Expected: ${this.data.taskUuid}, Received: ${updatedTask.uuid}`
          );
          return;
        }

        // Update the task signal with the new data from Pusher
        this.task.set(updatedTask);

        // Update the time inputs if they've changed
        const startHours = getHours(updatedTask.start_date);
        const startMinutes = getMinutes(updatedTask.start_date);
        const endHours = getHours(updatedTask.end_date);
        const endMinutes = getMinutes(updatedTask.end_date);
        this.startUpdated({ hours: startHours, minutes: startMinutes });
        this.endUpdated({ hours: endHours, minutes: endMinutes });
      });
  }

  #fetchTaskDetails(uuid: string): void {
    this.loadingTask.set(true);

    this.#taskService.getTask(uuid).subscribe((task) => {
      this.task.set(task);

      const startHours = getHours(task.start_date);
      const startMinutes = getMinutes(task.start_date);
      const endHours = getHours(task.end_date);
      const endMinutes = getMinutes(task.end_date);
      this.startUpdated({ hours: startHours, minutes: startMinutes });
      this.endUpdated({ hours: endHours, minutes: endMinutes });

      this.loadingTask.set(false);

      // Fetch property data with smartlock information
      if (task.property?.id) {
        this.#fetchPropertySmartlockData(task.property.id);
      }
    });
  }

  #fetchPropertySmartlockData(propertyId: number): void {
    this.loadingProperty.set(true);

    this.#propertiesService.getProperty(propertyId, [PropertyIncludes.HasSmartlocks]).subscribe({
      next: (property) => {
        this.propertyWithSmartlocks.set(property);
        this.loadingProperty.set(false);
      },
      error: (error) => {
        Logger.error('Failed to fetch property smartlock data', error);
        this.loadingProperty.set(false);
      },
    });
  }
}
